import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Search, Users, Gift, CheckCircle2, Clock, Sparkles, Zap, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const t = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return t ? { 'Authorization': `Bearer ${t}` } : {};
};

export default function AdminInsiders() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ insiders: [], count: 0, rewards_pending: 0, rewards_fulfilled: 0 });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [markLoading, setMarkLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideLoading, setOverrideLoading] = useState(false);

  const submitOverride = (revoke = false) => {
    if (!selected) return;
    if (!revoke && !overrideReason.trim()) return toast.error('Please provide a reason');
    setOverrideLoading(true);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/admin/insiders/${selected.user_id}/override-verify`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(getAuthHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setOverrideLoading(false);
      if (xhr.status === 200) {
        toast.success(revoke ? 'Override revoked — back to pending' : 'Insider activated via admin override');
        setOverrideOpen(false);
        setOverrideReason('');
        // Refresh detail + list
        setSelected(null);
        fetchAll();
      } else if (xhr.status === 422) {
        try { const err = JSON.parse(xhr.responseText); toast.error(err.detail || 'Invalid input'); } catch { toast.error('Invalid input'); }
      } else {
        toast.error('Failed to update override');
      }
    };
    xhr.onerror = () => { setOverrideLoading(false); toast.error('Network error'); };
    xhr.send(JSON.stringify({ reason: overrideReason.trim(), revoke }));
  };

  useEffect(() => { fetchAll(); }, []);

  const runBackfill = () => {
    if (!window.confirm('Scan all Shopify orders and auto-attribute anyone who used an insider discount code. This is safe to run multiple times.\n\nProceed?')) return;
    setBackfilling(true);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/admin/insiders/backfill-attributions`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(getAuthHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setBackfilling(false);
      if (xhr.status === 200) {
        const r = JSON.parse(xhr.responseText);
        toast.success(`Scanned ${r.scanned} orders — attributed ${r.attributed}, linked ${r.linked_existing}, sent ${r.reward_emails_sent} reward email(s).`, { duration: 8000 });
        fetchAll();
      } else toast.error('Backfill failed');
    };
    xhr.onerror = () => { setBackfilling(false); toast.error('Network error'); };
    xhr.send('{}');
  };

  const fetchAll = () => {
    setLoading(true);
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/admin/insiders`, true);
    Object.entries(getAuthHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setLoading(false);
      if (xhr.status === 200) setData(JSON.parse(xhr.responseText));
      else toast.error('Failed to load insiders');
    };
    xhr.onerror = () => { setLoading(false); toast.error('Network error'); };
    xhr.send();
  };

  const openDetail = (insider) => {
    setSelected(insider);
    setDetail(null);
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/admin/insiders/${insider.user_id}`, true);
    Object.entries(getAuthHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => { if (xhr.status === 200) setDetail(JSON.parse(xhr.responseText)); };
    xhr.send();
  };

  const markReward = (fulfilled) => {
    if (!selected) return;
    setMarkLoading(true);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/admin/insiders/${selected.user_id}/mark-reward`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(getAuthHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setMarkLoading(false);
      if (xhr.status === 200) {
        toast.success(fulfilled ? 'Marked as fulfilled' : 'Reset to unfulfilled');
        setSelected(null);
        fetchAll();
      } else toast.error('Failed to update reward status');
    };
    xhr.onerror = () => { setMarkLoading(false); toast.error('Network error'); };
    xhr.send(JSON.stringify({ fulfilled }));
  };

  const filtered = (data.insiders || []).filter(i => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (
      (i.name || '').toLowerCase().includes(q) ||
      (i.email || '').toLowerCase().includes(q) ||
      (i.referral_code || '').toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-amber-500" />
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>KBInsiders</h1>
        </div>
        <Button onClick={runBackfill} disabled={backfilling} variant="outline" data-testid="backfill-attributions-btn">
          <Zap className="w-4 h-4 mr-1.5 text-amber-500" />
          {backfilling ? 'Scanning...' : 'Backfill Shopify attributions'}
        </Button>
      </div>
      <p className="text-muted-foreground mb-6">Customers earning free protein through referrals.</p>

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Card className="p-6" data-testid="insider-total-card">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
            <Users className="w-4 h-4" /> Total Insiders
          </div>
          <div className="text-3xl font-bold">{data.count}</div>
        </Card>
        <Card className="p-6" data-testid="insider-pending-card">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
            <Clock className="w-4 h-4 text-amber-500" /> Rewards to fulfil
          </div>
          <div className="text-3xl font-bold text-amber-600">{data.rewards_pending}</div>
        </Card>
        <Card className="p-6" data-testid="insider-fulfilled-card">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" /> Rewards delivered
          </div>
          <div className="text-3xl font-bold text-green-600">{data.rewards_fulfilled}</div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="insider-search"
          placeholder="Search by name, email, or code..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9 max-w-md"
        />
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading insiders...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">No insiders found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="p-3 font-semibold">Name</th>
                  <th className="p-3 font-semibold">Email</th>
                  <th className="p-3 font-semibold">Code</th>
                  <th className="p-3 font-semibold text-right">Referrals</th>
                  <th className="p-3 font-semibold text-right">Transacting</th>
                  <th className="p-3 font-semibold text-right">Revenue</th>
                  <th className="p-3 font-semibold">Reward</th>
                  <th className="p-3 font-semibold">Joined</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(i => (
                  <tr
                    key={i.user_id}
                    onClick={() => openDetail(i)}
                    className="border-t hover:bg-muted/30 cursor-pointer"
                    data-testid={`insider-row-${i.user_id}`}
                  >
                    <td className="p-3 font-medium">{i.name || '—'}</td>
                    <td className="p-3 text-muted-foreground">{i.email}</td>
                    <td className="p-3 font-mono text-xs">{i.referral_code}</td>
                    <td className="p-3 text-right">{i.total_referrals}</td>
                    <td className="p-3 text-right">
                      <span className={i.transacting_referrals >= 5 ? 'text-green-600 font-semibold' : ''}>
                        {i.transacting_referrals}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <span className={i.total_revenue >= 10000 ? 'text-green-600 font-semibold' : ''}>
                        ₹{Math.round(i.total_revenue).toLocaleString('en-IN')}
                      </span>
                    </td>
                    <td className="p-3">
                      {i.reward_fulfilled ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Delivered</Badge>
                      ) : i.reward_earned ? (
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                          <Gift className="w-3 h-3 mr-1" /> Ready to ship
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">In progress</Badge>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground text-xs">
                      {i.created_at ? new Date(i.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selected?.name || 'Insider'}</DialogTitle>
            <DialogDescription>{selected?.email}</DialogDescription>
          </DialogHeader>
          {!detail ? (
            <div className="p-6 text-center text-muted-foreground">Loading...</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Code: </span><span className="font-mono">{detail.insider.referral_code}</span></div>
                <div><span className="text-muted-foreground">Flavour: </span>{detail.insider.flavor_preference || '—'}</div>
                <div><span className="text-muted-foreground">Referrals: </span>{detail.total_referrals}</div>
                <div><span className="text-muted-foreground">Transacting: </span>{detail.transacting_referrals} / {detail.required_referrals}</div>
                <div><span className="text-muted-foreground">Revenue: </span>₹{Math.round(detail.total_revenue).toLocaleString('en-IN')} / ₹{detail.reward_threshold.toLocaleString('en-IN')}</div>
                <div><span className="text-muted-foreground">Signup orders: </span>{detail.insider.shopify_orders_at_signup ?? '—'}</div>
                <div className="col-span-2"><span className="text-muted-foreground">Status: </span>
                  {detail.insider.status === 'active' ? (
                    <Badge className="bg-green-100 text-green-700">Active</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700">Pending verification</Badge>
                  )}
                  {detail.insider.verified_contact && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      via <strong>{detail.insider.verified_contact.type}</strong>
                      {detail.insider.verified_contact.type === 'admin_override'
                        ? ` — "${detail.insider.verified_contact.value}"`
                        : `: ${detail.insider.verified_contact.value}`}
                    </span>
                  )}
                </div>
                {detail.insider.overridden_by && (
                  <div className="col-span-2 text-xs text-muted-foreground">
                    Overridden by <strong>{detail.insider.overridden_by}</strong> on {new Date(detail.insider.overridden_at).toLocaleDateString()}
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-semibold mb-2 text-sm">Referrals ({detail.referrals.length})</h4>
                <div className="border rounded-md max-h-64 overflow-y-auto">
                  {detail.referrals.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">No referrals yet</div>
                  ) : (
                    detail.referrals.map(r => (
                      <div key={r.referral_id} className="p-3 border-b last:border-0 flex items-center justify-between text-sm">
                        <div>
                          <div className="font-medium">{r.customer_name}</div>
                          <div className="text-xs text-muted-foreground">{r.customer_email || r.customer_phone || '—'}</div>
                        </div>
                        <div className="text-right">
                          {r.is_transacting ? (
                            <>
                              <div className="font-semibold text-green-600">₹{Math.round(r.orders_total).toLocaleString('en-IN')}</div>
                              <div className="text-xs text-muted-foreground">{r.orders_count} orders</div>
                            </>
                          ) : (
                            <Badge variant="outline" className="text-xs">Pending</Badge>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <DialogFooter className="gap-2 sm:justify-between">
                <div className="text-xs text-muted-foreground">
                  {detail.reward_earned ? (
                    <span className="text-amber-600 font-semibold">🎁 Reward earned — {detail.insider.flavor_preference || 'flavour pending'}</span>
                  ) : (
                    <span>Not yet eligible for reward</span>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {detail.insider.status !== 'active' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setOverrideReason(''); setOverrideOpen(true); }}
                      data-testid="admin-override-btn"
                      className="border-amber-400 text-amber-700 hover:bg-amber-50"
                    >
                      <ShieldCheck className="w-4 h-4 mr-1.5" /> Admin override — activate
                    </Button>
                  )}
                  {detail.insider.status === 'active' && detail.insider.verified_contact?.type === 'admin_override' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => submitOverride(true)}
                      disabled={overrideLoading}
                      data-testid="admin-revoke-override-btn"
                      className="text-amber-700"
                    >
                      Revoke override
                    </Button>
                  )}
                  {selected?.reward_fulfilled ? (
                    <Button variant="outline" size="sm" onClick={() => markReward(false)} disabled={markLoading} data-testid="mark-unfulfilled-btn">
                      Reset status
                    </Button>
                  ) : (
                    <Button
                      onClick={() => markReward(true)}
                      disabled={markLoading || !detail.reward_earned}
                      data-testid="mark-fulfilled-btn"
                      className="bg-amber-500 hover:bg-amber-600 text-black"
                    >
                      <Gift className="w-4 h-4 mr-1.5" /> Mark reward delivered
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin override dialog */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Activate via admin override</DialogTitle>
            <DialogDescription>
              This will bypass the 3-order Shopify rule and mark the insider as active immediately. Your decision + reason are logged for audit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-sm">Reason for override *</Label>
            <Textarea
              value={overrideReason}
              onChange={e => setOverrideReason(e.target.value)}
              placeholder="e.g. VIP customer onboarding, team member, promo campaign, orders placed before Shopify sync..."
              rows={3}
              data-testid="override-reason-input"
            />
            <p className="text-xs text-muted-foreground">
              The insider will see their account as verified "via admin override" in their dashboard.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOverrideOpen(false)}>Cancel</Button>
            <Button
              onClick={() => submitOverride(false)}
              disabled={overrideLoading || !overrideReason.trim()}
              data-testid="override-submit-btn"
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              <ShieldCheck className="w-4 h-4 mr-1.5" />
              {overrideLoading ? 'Activating...' : 'Activate insider'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
